<template>
  <div
    class="schedule-event"
    :class="{
      'schedule-event--compact': compact,
      'schedule-event--squeezed': squeezed,
    }"
    :style="{ color: colors.onContainer, backgroundColor: colors.container }"
  >
    <div class="schedule-event__row is-time">
      <VIcon
        v-if="!compact && !squeezed"
        icon="mdi-clock-time-four-outline"
        class="schedule-event__icon"
      />
      <span class="schedule-event__time-text">{{ timeRange }}</span>
      <span
        v-if="withNames && !compact && !squeezed"
        class="schedule-event__name"
      >
        {{ withNames }}
      </span>
    </div>

    <div class="schedule-event__row is-title">{{ title }}</div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { CalendarEventExternal } from "@schedule-x/calendar";
import type { Temporal } from "temporal-polyfill";
import { Reservation, ReservationStatus } from "@/main/types/reservation.type";
import { RESERVATION_STATUS_COLORS } from "@/main/helpers/reservation-status.helper";

const props = withDefaults(
  defineProps<{
    calendarEvent: CalendarEventExternal;
    compact?: boolean;
    // Pixels per minute of the time grid this event renders in (see
    // Schedules.vue's weekOptions.gridHeight/dayBoundaries) - lets a short
    // reservation's box know its own rendered height ahead of layout, so
    // it can drop down to a single line instead of `overflow: hidden`
    // silently cutting the time off when title + time don't both fit.
    pxPerMinute?: number;
  }>(),
  { compact: false, pxPerMinute: undefined }
);

const reservation = computed(
  () => props.calendarEvent.data as Reservation | undefined
);

const title = computed(() => props.calendarEvent.title || "");

const withNames = computed(
  () =>
    reservation.value?.customers?.map((customer) => customer.name).join(", ") ||
    ""
);

const colors = computed(
  () =>
    RESERVATION_STATUS_COLORS[
      (props.calendarEvent.calendarId as ReservationStatus) ||
        ReservationStatus.Created
    ]
);

const pad = (n: number) => n.toString().padStart(2, "0");
// Reservation events are always created with a Temporal.ZonedDateTime
// (never a bare PlainDate, since every reservation has a specific time).
const format = (zdt: Temporal.ZonedDateTime) => `${pad(zdt.hour)}:${pad(zdt.minute)}`;

const timeRange = computed(
  () =>
    `${format(props.calendarEvent.start as Temporal.ZonedDateTime)} - ${format(
      props.calendarEvent.end as Temporal.ZonedDateTime
    )}`
);

// Below this estimated height there's no room for title and time on their
// own lines - collapse to a single line instead (see the template).
const MIN_HEIGHT_FOR_TWO_LINES = 42;

const squeezed = computed(() => {
  if (props.compact || !props.pxPerMinute) return false;

  const start = props.calendarEvent.start as Temporal.ZonedDateTime;
  const end = props.calendarEvent.end as Temporal.ZonedDateTime;
  const durationMinutes = (end.epochMilliseconds - start.epochMilliseconds) / 60_000;

  return durationMinutes * props.pxPerMinute < MIN_HEIGHT_FOR_TWO_LINES;
});
</script>

<style lang="scss" scoped>
.schedule-event {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 4px;
  cursor: pointer;
  box-sizing: border-box;
  user-select: none;
  overflow: hidden;
  padding: 5px;
  font-size: 12px;

  &__row {
    display: flex;
    align-items: flex-start;
    column-gap: 4px;

    &.is-title {
      opacity: 0.8;
    }
  }

  &__icon {
    font-size: 12px;
    margin-top: 2px;
  }

  // The hour range never shrinks/truncates - only the name next to it does,
  // via __name below.
  &__time-text {
    flex: none;
    white-space: nowrap;
  }

  &__name {
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
    flex: 1 1 auto;
  }

  // Too short a box for time + title on their own lines (see `squeezed` in
  // the script) - single row instead, time first so it's the title that
  // gets truncated if space is still tight, not the time.
  &--squeezed {
    display: flex;
    align-items: baseline;
    flex-wrap: nowrap;
    gap: 4px;

    .schedule-event__row {
      white-space: nowrap;
    }

    .schedule-event__row.is-title {
      overflow: hidden;
      text-overflow: ellipsis;
      min-width: 0;
    }

    .schedule-event__row.is-time {
      flex: 0 0 auto;
    }
  }

  &--compact {
    padding: 2px 4px;
    font-size: 11px;

    .schedule-event__row {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      &.is-time {
        opacity: 0.8;
      }
    }
  }
}
</style>
